import type { SignalDefinition, SignalDefinitionStatus, SignalOrigin, SignalTierClass } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { FlaskConical, PenLine, Plus, Sparkles } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import { SignalDefinitionStatusBadge } from "@/components/domain/analytics";
import { SectionTitle } from "@/components/domain/misc";
import { ErrorPanel } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { Select } from "@/components/ui/input";
import { EmptyState, Skeleton } from "@/components/ui/misc";
import { Tooltip } from "@/components/ui/tooltip";
import { definitionColumns } from "@/features/signals/definition-columns";
import { ORIGIN_META, STATUS_HELP, STATUS_ORDER } from "@/features/signals/meta";
import { ProposalCard } from "@/features/signals/proposal-card";
import { q } from "@/lib/api";
import { cn } from "@/lib/utils";

const search = z.object({
  status: z.enum(STATUS_ORDER as [SignalDefinitionStatus, ...SignalDefinitionStatus[]]).optional().catch(undefined),
  origin: z.enum(["catalog", "pack", "discovered", "custom"]).optional().catch(undefined),
  tier: z.enum(["risk", "analytics"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/signals/studio/")({
  validateSearch: search,
  component: StudioPage,
});

function StudioPage() {
  const { status, origin, tier } = Route.useSearch();
  const navigate = Route.useNavigate();
  const defs = useQuery(q.signalDefinitions());
  const agents = useQuery(q.agents());
  const names = useMemo(() => new Map(agents.data?.map((a) => [a.id, a.name])), [agents.data]);
  const columns = useMemo(() => definitionColumns(names), [names]);

  const proposals = useMemo(() => defs.data?.filter((d) => d.status === "proposed"), [defs.data]);
  const scoped = useMemo(() => defs.data?.filter((d) => (!origin || d.origin === origin) && (!tier || d.tierClass === tier)), [defs.data, origin, tier]);
  const statusCounts = useMemo(() => {
    const m = new Map<SignalDefinitionStatus, number>();
    for (const d of scoped ?? []) m.set(d.status, (m.get(d.status) ?? 0) + 1);
    return m;
  }, [scoped]);
  const rows = useMemo(() => (status ? scoped?.filter((d) => d.status === status) : scoped), [scoped, status]);
  const set = (patch: Partial<z.infer<typeof search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true, resetScroll: false });

  return (
    <div className="flex flex-col gap-6">
      <section aria-labelledby="proposals-title">
        <SectionTitle
          actions={
            <span className="text-xs text-fg-3">Discovery runs nightly over failed, escalated and frustrated sessions that no signal explains.</span>
          }
        >
          <span id="proposals-title" className="inline-flex items-center gap-1.5">
            <Sparkles className="size-3.5 text-accent" aria-hidden /> Proposals {proposals ? <span className="tabular text-fg-2">· {proposals.length}</span> : null}
          </span>
        </SectionTitle>
        {defs.error ? (
          <ErrorPanel error={defs.error} reset={() => void defs.refetch()} />
        ) : !proposals ? (
          <div className="grid gap-3 lg:grid-cols-2">
            {Array.from({ length: 2 }, (_, i) => (
              <Skeleton key={i} className="h-[380px]" />
            ))}
          </div>
        ) : proposals.length ? (
          <div className="grid gap-3 lg:grid-cols-2">
            {proposals.map((d) => (
              <ProposalCard key={d.id} def={d} agentNames={names} />
            ))}
          </div>
        ) : (
          <Card>
            <EmptyState
              icon={<Sparkles />}
              title="No new proposals"
              description="When discovery finds a recurring pattern that existing signals don't explain, it shows up here with evidence."
              action={
                <Button size="sm" variant="outline" asChild className="mt-1">
                  <Link to="/signals/studio/new">
                    <PenLine /> Write a signal
                  </Link>
                </Button>
              }
            />
          </Card>
        )}
      </section>

      <Card>
        <CardHeader
          title="All definitions"
          description={rows ? `${rows.length} of ${defs.data?.length ?? 0} definitions · cost is per 1,000 sessions` : "Loading…"}
          actions={
            <>
              <Select aria-label="Origin" value={origin ?? ""} onChange={(e) => set({ origin: (e.target.value || undefined) as SignalOrigin | undefined })} className="w-36">
                <option value="">All origins</option>
                {(Object.keys(ORIGIN_META) as SignalOrigin[]).map((o) => (
                  <option key={o} value={o}>
                    {ORIGIN_META[o].label}
                  </option>
                ))}
              </Select>
              <Select aria-label="Tier" value={tier ?? ""} onChange={(e) => set({ tier: (e.target.value || undefined) as SignalTierClass | undefined })} className="w-36">
                <option value="">All tiers</option>
                <option value="analytics">Analytics</option>
                <option value="risk">Risk</option>
              </Select>
              <Button size="sm" asChild>
                <Link to="/signals/studio/new">
                  <Plus /> New signal
                </Link>
              </Button>
            </>
          }
        />
        <div role="group" aria-label="Filter by status" className="flex flex-wrap gap-1.5 border-b px-4 py-2.5">
          <StatusPill active={!status} onClick={() => set({ status: undefined })} label="All" count={scoped?.length} />
          {STATUS_ORDER.map((s) => (
            <Tooltip key={s} content={STATUS_HELP[s]}>
              <span>
                <StatusPill active={status === s} onClick={() => set({ status: status === s ? undefined : s })} status={s} count={statusCounts.get(s) ?? 0} />
              </span>
            </Tooltip>
          ))}
        </div>
        {defs.error ? (
          <ErrorPanel error={defs.error} reset={() => void defs.refetch()} className="m-4" />
        ) : (
          <DataTable<SignalDefinition>
            data={rows}
            loading={defs.isPending}
            columns={columns}
            dense
            stickyHeader={false}
            getRowId={(d) => d.id}
            onRowClick={(d) => void navigate({ to: "/signals/studio/$definitionId", params: { definitionId: d.id } })}
            empty={{ icon: <FlaskConical />, title: "No definitions match", description: "Clear the status, origin or tier filter." }}
          />
        )}
      </Card>
    </div>
  );
}

function StatusPill({ active, onClick, status, label, count }: { active: boolean; onClick: () => void; status?: SignalDefinitionStatus; label?: string; count: number | undefined }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors",
        active ? "border-accent/40 bg-accent-soft text-fg" : "border-border bg-surface text-fg-2 hover:bg-surface-2 hover:text-fg",
        count === 0 && !active && "opacity-60",
      )}
    >
      {status ? <SignalDefinitionStatusBadge status={status} /> : label}
      <span className="tabular text-fg-3">{count ?? ""}</span>
    </button>
  );
}
